import { useEffect, useMemo, useState } from "react";
import { Lock, Plus, PowerOff, UserX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { addDays, formatCourtLabel, toLocalDateStr } from "./helpers";
import { colorForSport, type SportColorClasses } from "./sport-color";
import type { MeCourt, MeSport } from "@/lib/me";

export interface CalendarBooking {
  id: string;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  endTime: string;
  status: "BOOKED" | "CANCELLED";
  noShow: boolean;
  playerName: string;
  courtId: string;
  courtName: string;
  sportId: string;
  sportName: string;
}

export interface CalendarBlock {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  reason: string;
  courtId: string;
  courtName: string;
  sportId: string;
  seriesId: string | null;
}

export interface CalendarClosure {
  date: string;
  reason: string;
}

interface Props {
  weekStart: Date;
  bookings: CalendarBooking[];
  /** Optional blocks rendered as striped cells alongside bookings. */
  blocks?: CalendarBlock[];
  /** Whole-day club closures rendered as a banner on the day column. */
  closures?: CalendarClosure[];
  courts: MeCourt[];
  sports: MeSport[];
  sessionDurationMinutes: number;
  workingHoursStart: string; // HH:mm
  workingHoursEnd: string; // HH:mm
  todayStr: string;
  /** Sport ID to filter to. Undefined / null = show all sports in scope. */
  sportFilter?: string | null;
  onCellAdd: (date: string, time: string) => void;
  onBookingClick: (bookingId: string) => void;
  onBlockClick?: (blockId: string) => void;
  onClosureClick?: (date: string) => void;
}

interface CellData {
  bookings: CalendarBooking[];
  blocks: CalendarBlock[];
  free: boolean;
}

interface DayInfo {
  dayKey: "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
  date: string;
  dateNum: number;
  isToday: boolean;
}

function parseHHMMToMin(s: string): number {
  const [h, m] = s.split(":");
  return parseInt(h, 10) * 60 + parseInt(m, 10);
}

function formatMinToHHMM(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export default function WeeklyCalendar({
  weekStart,
  bookings,
  blocks = [],
  closures = [],
  courts,
  sports,
  sessionDurationMinutes,
  workingHoursStart,
  workingHoursEnd,
  todayStr,
  sportFilter,
  onCellAdd,
  onBookingClick,
  onBlockClick,
  onClosureClick,
}: Props) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState<string | null>(null);

  const dayKeys: DayInfo["dayKey"][] = [
    "mon",
    "tue",
    "wed",
    "thu",
    "fri",
    "sat",
    "sun",
  ];
  const days = useMemo<DayInfo[]>(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = addDays(weekStart, i);
        return {
          dayKey: dayKeys[i],
          date: toLocalDateStr(d),
          dateNum: d.getDate(),
          isToday: toLocalDateStr(d) === todayStr,
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekStart, todayStr],
  );

  // Phone day-view: which day is currently shown.
  const [activeDayIndex, setActiveDayIndex] = useState(0);
  useEffect(() => {
    const todayIdx = days.findIndex((d) => d.isToday);
    setActiveDayIndex(todayIdx >= 0 ? todayIdx : 0);
  }, [days]);

  // Session-step rows from start until <= end - duration.
  const slotStarts = useMemo<string[]>(() => {
    const out: string[] = [];
    const startMin = parseHHMMToMin(workingHoursStart);
    const endMin = parseHHMMToMin(workingHoursEnd);
    if (sessionDurationMinutes <= 0) return out;
    for (
      let m = startMin;
      m + sessionDurationMinutes <= endMin;
      m += sessionDurationMinutes
    ) {
      out.push(formatMinToHHMM(m));
    }
    return out;
  }, [workingHoursStart, workingHoursEnd, sessionDurationMinutes]);

  const filteredCourts = useMemo(
    () =>
      sportFilter
        ? courts.filter((c) => c.sportId === sportFilter)
        : courts.filter((c) => c.isActive),
    [courts, sportFilter],
  );

  const filteredBookings = useMemo(
    () =>
      sportFilter
        ? bookings.filter((b) => b.sportId === sportFilter)
        : bookings,
    [bookings, sportFilter],
  );

  const filteredBlocks = useMemo(
    () =>
      sportFilter ? blocks.filter((b) => b.sportId === sportFilter) : blocks,
    [blocks, sportFilter],
  );

  const closureByDate = useMemo(() => {
    const m = new Map<string, CalendarClosure>();
    for (const c of closures) m.set(c.date, c);
    return m;
  }, [closures]);

  // Stable "now" reference for the lifetime of this render so the "past slot"
  // calculation doesn't drift mid-paint. Re-runs on prop change is fine.
  const nowMinutes = useMemo(() => {
    const n = new Date();
    return n.getHours() * 60 + n.getMinutes();
  }, [todayStr]);

  function isPastSlot(date: string, slot: string): boolean {
    if (date < todayStr) return true;
    if (date > todayStr) return false;
    return parseHHMMToMin(slot) <= nowMinutes;
  }

  // Pre-compute (date, slot) → { bookings + blocks starting here, free? }.
  const cellData = useMemo(() => {
    const map = new Map<string, CellData>();

    const courtIds = new Set(filteredCourts.map((c) => c.id));
    const bookingsByDate = new Map<string, CalendarBooking[]>();
    for (const b of filteredBookings) {
      if (!courtIds.has(b.courtId)) continue;
      const arr = bookingsByDate.get(b.date) ?? [];
      arr.push(b);
      bookingsByDate.set(b.date, arr);
    }
    const blocksByDate = new Map<string, CalendarBlock[]>();
    for (const b of filteredBlocks) {
      if (!courtIds.has(b.courtId)) continue;
      const arr = blocksByDate.get(b.date) ?? [];
      arr.push(b);
      blocksByDate.set(b.date, arr);
    }

    for (const d of days) {
      const dayBookings = bookingsByDate.get(d.date) ?? [];
      const dayBlocks = blocksByDate.get(d.date) ?? [];
      for (const slot of slotStarts) {
        const slotMin = parseHHMMToMin(slot);
        const slotEndMin = slotMin + sessionDurationMinutes;

        const cellBookings: CalendarBooking[] = [];
        const cellBlocks: CalendarBlock[] = [];
        const occupied = new Set<string>();
        for (const b of dayBookings) {
          const bsMin = parseHHMMToMin(b.startTime);
          const beMin = parseHHMMToMin(b.endTime);
          if (bsMin === slotMin) cellBookings.push(b);
          if (bsMin < slotEndMin && beMin > slotMin) occupied.add(b.courtId);
        }
        for (const bl of dayBlocks) {
          const bsMin = parseHHMMToMin(bl.startTime);
          const beMin = parseHHMMToMin(bl.endTime);
          if (bsMin === slotMin) cellBlocks.push(bl);
          if (bsMin < slotEndMin && beMin > slotMin) occupied.add(bl.courtId);
        }

        const free =
          filteredCourts.length > 0 && occupied.size < filteredCourts.length;

        map.set(`${d.date}-${slot}`, {
          bookings: cellBookings,
          blocks: cellBlocks,
          free,
        });
      }
    }
    return map;
  }, [
    days,
    slotStarts,
    filteredBookings,
    filteredBlocks,
    filteredCourts,
    sessionDurationMinutes,
  ]);

  function colorClassesFor(b: CalendarBooking): SportColorClasses {
    return colorForSport(b.sportId, sports);
  }

  return (
    <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
      {/* Phone day-picker pill row (visible on <md only) */}
      <div className="md:hidden flex gap-2 overflow-x-auto px-3 py-3 border-b border-neutral-200 bg-neutral-50">
        {days.map((d, i) => {
          const active = i === activeDayIndex;
          return (
            <button
              key={d.date}
              type="button"
              onClick={() => setActiveDayIndex(i)}
              className={`flex-shrink-0 flex flex-col items-center justify-center min-w-[56px] px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                active
                  ? "bg-primary-600 text-white shadow-sm"
                  : d.isToday
                    ? "bg-primary-50 text-primary-700 border border-primary-200"
                    : "bg-white text-neutral-600 border border-neutral-200"
              }`}
            >
              <span
                className={`text-[11px] font-medium uppercase tracking-wide ${
                  active ? "text-white/80" : "text-neutral-400"
                }`}
              >
                {t(`days.short.${d.dayKey}`)}
              </span>
              <span className="text-base font-bold leading-none mt-0.5">
                {d.dateNum}
              </span>
            </button>
          );
        })}
      </div>

      {/* Phone day-view grid (single column) */}
      <div className="md:hidden overflow-x-auto">
        <DayGrid
          day={days[activeDayIndex] ?? days[0]}
          slotStarts={slotStarts}
          cellData={cellData}
          hovered={hovered}
          setHovered={setHovered}
          closure={closureByDate.get(
            (days[activeDayIndex] ?? days[0])?.date ?? "",
          )}
          onCellAdd={onCellAdd}
          onBookingClick={onBookingClick}
          onBlockClick={onBlockClick}
          onClosureClick={onClosureClick}
          colorClassesFor={colorClassesFor}
          isPastSlot={isPastSlot}
        />
      </div>

      {/* Tablet/desktop week grid (visible on md+) */}
      <div className="hidden md:block overflow-x-auto">
        <div className="min-w-0 lg:min-w-[800px]">
          {/* Header row */}
          <div className="grid grid-cols-[80px_repeat(7,minmax(0,1fr))] border-b border-neutral-200 bg-neutral-50">
            <div className="px-3 py-3 text-xs font-medium text-neutral-400">
              {t("bookings.calendar.time")}
            </div>
            {days.map((d) => {
              const closure = closureByDate.get(d.date);
              return (
                <div
                  key={d.date}
                  className={`px-3 py-3 text-center border-l border-neutral-200 ${
                    closure ? "bg-red-50" : d.isToday ? "bg-primary-50" : ""
                  }`}
                >
                  <div
                    className={`text-sm font-semibold ${
                      closure
                        ? "text-red-600"
                        : d.isToday
                          ? "text-primary-600"
                          : "text-neutral-800"
                    }`}
                  >
                    {t(`days.short.${d.dayKey}`)}
                  </div>
                  <div
                    className={`text-xs ${
                      closure ? "text-red-500" : "text-neutral-500"
                    }`}
                  >
                    {d.dateNum}
                  </div>
                  {closure && (
                    <button
                      type="button"
                      onClick={() => onClosureClick?.(d.date)}
                      title={closure.reason}
                      className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-red-700 bg-red-100 hover:bg-red-200 rounded-full px-2 py-0.5 cursor-pointer truncate max-w-full"
                    >
                      <PowerOff className="w-3 h-3 shrink-0" />
                      <span className="truncate">{closure.reason}</span>
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Time rows */}
          {slotStarts.map((slot) => (
            <div
              key={slot}
              className="grid grid-cols-[80px_repeat(7,minmax(0,1fr))] border-b border-neutral-100 min-h-[60px] lg:min-h-[72px]"
            >
              <div className="px-3 py-2 text-xs text-neutral-400 bg-neutral-50/50">
                {slot}
              </div>
              {days.map((d) => {
                const cellKey = `${d.date}-${slot}`;
                const data = cellData.get(cellKey);
                const cellBookings = data?.bookings ?? [];
                const cellBlocks = data?.blocks ?? [];
                const free = data?.free ?? false;
                const isClosed = closureByDate.has(d.date);
                return (
                  <div
                    key={cellKey}
                    className={`relative border-l border-neutral-100 p-1 lg:p-1.5 group ${
                      isClosed
                        ? "bg-red-50/40"
                        : d.isToday
                          ? "bg-primary-50/30"
                          : ""
                    }`}
                    onMouseEnter={() => setHovered(cellKey)}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <div className="space-y-1">
                      {cellBookings.map((b) => {
                        const c = colorClassesFor(b);
                        const isNoShow = b.status === "BOOKED" && b.noShow;
                        return (
                          <button
                            key={b.id}
                            onClick={() => onBookingClick(b.id)}
                            className={`w-full text-left rounded-md px-1.5 py-1 lg:px-2 lg:py-1.5 border-l-[3px] cursor-pointer transition-colors ${
                              b.status === "CANCELLED"
                                ? "bg-neutral-50 border-neutral-300 text-neutral-400 line-through hover:bg-neutral-100"
                                : isNoShow
                                  ? "bg-amber-100 border-amber-500 hover:bg-amber-200"
                                  : `${c.bg} ${c.border} ${c.bgHover}`
                            }`}
                          >
                            <div className="flex items-center gap-1 text-[11px] lg:text-xs font-semibold text-neutral-800 leading-tight">
                              {isNoShow && (
                                <UserX className="w-3 h-3 shrink-0 text-amber-600" />
                              )}
                              <span className="truncate">{b.playerName}</span>
                            </div>
                            <div className="hidden lg:block text-[10px] text-neutral-500 truncate">
                              {b.startTime} ·{" "}
                              {formatCourtLabel(
                                b.courtName,
                                t("bookings.form.court"),
                              )}
                            </div>
                          </button>
                        );
                      })}
                      {cellBlocks.map((bl) => (
                        <button
                          key={bl.id}
                          onClick={() => onBlockClick?.(bl.id)}
                          title={bl.reason}
                          className="w-full text-left rounded-md px-1.5 py-1 lg:px-2 lg:py-1.5 border-l-[3px] border-red-400 bg-red-50 hover:bg-red-100 cursor-pointer transition-colors bg-[repeating-linear-gradient(45deg,_rgba(239,68,68,0.06)_0,_rgba(239,68,68,0.06)_6px,_transparent_6px,_transparent_12px)]"
                        >
                          <div className="flex items-center gap-1 text-[11px] lg:text-xs font-semibold text-red-700 leading-tight truncate">
                            <Lock className="w-3 h-3 shrink-0" />
                            <span className="truncate">{bl.reason}</span>
                          </div>
                          <div className="hidden lg:block text-[10px] text-red-500/80 truncate">
                            {bl.startTime} ·{" "}
                            {formatCourtLabel(
                              bl.courtName,
                              t("bookings.form.court"),
                            )}
                          </div>
                        </button>
                      ))}
                    </div>

                    {free &&
                      hovered === cellKey &&
                      !isPastSlot(d.date, slot) &&
                      !isClosed && (
                        <button
                          onClick={() => onCellAdd(d.date, slot)}
                          className="absolute bottom-1.5 right-1.5 w-7 h-7 bg-primary-600 hover:bg-primary-700 text-white rounded-full flex items-center justify-center shadow-md transition-all cursor-pointer z-10"
                          aria-label={t("bookings.calendar.addBooking", {
                            count: 0,
                          })}
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DayGrid({
  day,
  slotStarts,
  cellData,
  hovered,
  setHovered,
  closure,
  onCellAdd,
  onBookingClick,
  onBlockClick,
  onClosureClick,
  colorClassesFor,
  isPastSlot,
}: {
  day: DayInfo;
  slotStarts: string[];
  cellData: Map<string, CellData>;
  hovered: string | null;
  setHovered: (v: string | null) => void;
  closure?: CalendarClosure;
  onCellAdd: (date: string, time: string) => void;
  onBookingClick: (id: string) => void;
  onBlockClick?: (id: string) => void;
  onClosureClick?: (date: string) => void;
  colorClassesFor: (b: CalendarBooking) => SportColorClasses;
  isPastSlot: (date: string, slot: string) => boolean;
}) {
  const { t } = useTranslation();
  if (!day) return null;

  return (
    <div className="min-w-0">
      <div className="grid grid-cols-[64px_minmax(0,1fr)] border-b border-neutral-200 bg-neutral-50">
        <div className="px-2 py-3 text-xs font-medium text-neutral-400">
          {t("bookings.calendar.time")}
        </div>
        <div
          className={`px-3 py-3 text-center border-l border-neutral-200 ${
            closure ? "bg-red-50" : day.isToday ? "bg-primary-50" : ""
          }`}
        >
          <div
            className={`text-sm font-semibold ${
              closure
                ? "text-red-600"
                : day.isToday
                  ? "text-primary-600"
                  : "text-neutral-800"
            }`}
          >
            {t(`days.short.${day.dayKey}`)} · {day.dateNum}
          </div>
        </div>
      </div>
      {closure && (
        <button
          type="button"
          onClick={() => onClosureClick?.(day.date)}
          className="w-full flex items-center gap-2 bg-red-50 border-b border-red-200 px-3 py-2 text-sm font-medium text-red-700 cursor-pointer hover:bg-red-100"
        >
          <PowerOff className="w-4 h-4 shrink-0" />
          <span className="truncate">{closure.reason}</span>
        </button>
      )}

      {slotStarts.map((slot) => {
        const cellKey = `${day.date}-${slot}`;
        const data = cellData.get(cellKey);
        const cellBookings = data?.bookings ?? [];
        const cellBlocks = data?.blocks ?? [];
        const free = data?.free ?? false;
        return (
          <div
            key={slot}
            className="grid grid-cols-[64px_minmax(0,1fr)] border-b border-neutral-100 min-h-[64px]"
          >
            <div className="px-2 py-2 text-xs text-neutral-400 bg-neutral-50/50">
              {slot}
            </div>
            <div
              className={`relative border-l border-neutral-100 p-2 ${
                day.isToday ? "bg-primary-50/30" : ""
              }`}
              onMouseEnter={() => setHovered(cellKey)}
              onMouseLeave={() => setHovered(null)}
            >
              <div className="space-y-1.5">
                {cellBookings.map((b) => {
                  const c = colorClassesFor(b);
                  const isNoShow = b.status === "BOOKED" && b.noShow;
                  return (
                    <button
                      key={b.id}
                      onClick={() => onBookingClick(b.id)}
                      className={`w-full text-left rounded-md px-3 py-2 border-l-[3px] cursor-pointer transition-colors ${
                        b.status === "CANCELLED"
                          ? "bg-neutral-50 border-neutral-300 text-neutral-400 line-through"
                          : isNoShow
                            ? "bg-amber-100 border-amber-500 active:bg-amber-200"
                            : `${c.bg} ${c.border} ${c.bgActive}`
                      }`}
                    >
                      <div className="flex items-center gap-1 text-sm font-semibold text-neutral-800 leading-tight">
                        {isNoShow && (
                          <UserX className="w-3.5 h-3.5 shrink-0 text-amber-600" />
                        )}
                        <span className="truncate">{b.playerName}</span>
                      </div>
                      <div className="text-xs text-neutral-500 truncate mt-0.5">
                        {b.startTime} ·{" "}
                        {formatCourtLabel(
                          b.courtName,
                          t("bookings.form.court"),
                        )}
                      </div>
                    </button>
                  );
                })}
                {cellBlocks.map((bl) => (
                  <button
                    key={bl.id}
                    onClick={() => onBlockClick?.(bl.id)}
                    title={bl.reason}
                    className="w-full text-left rounded-md px-3 py-2 border-l-[3px] border-red-400 bg-red-50 cursor-pointer bg-[repeating-linear-gradient(45deg,_rgba(239,68,68,0.06)_0,_rgba(239,68,68,0.06)_6px,_transparent_6px,_transparent_12px)]"
                  >
                    <div className="flex items-center gap-1 text-sm font-semibold text-red-700 leading-tight truncate">
                      <Lock className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{bl.reason}</span>
                    </div>
                    <div className="text-xs text-red-500/80 truncate mt-0.5">
                      {bl.startTime} ·{" "}
                      {formatCourtLabel(bl.courtName, t("bookings.form.court"))}
                    </div>
                  </button>
                ))}
              </div>

              {!closure &&
                free &&
                cellBookings.length === 0 &&
                cellBlocks.length === 0 &&
                !isPastSlot(day.date, slot) && (
                  <button
                    onClick={() => onCellAdd(day.date, slot)}
                    className="w-full h-full min-h-[48px] absolute inset-0 flex items-center justify-center text-neutral-300 hover:text-primary-600 hover:bg-primary-50/40 rounded transition-colors cursor-pointer"
                    aria-label={t("bookings.calendar.addBooking", { count: 0 })}
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                )}
              {!closure &&
                free &&
                cellBookings.length > 0 &&
                hovered === cellKey &&
                !isPastSlot(day.date, slot) && (
                  <button
                    onClick={() => onCellAdd(day.date, slot)}
                    className="absolute bottom-2 right-2 w-8 h-8 bg-primary-600 active:bg-primary-700 text-white rounded-full flex items-center justify-center shadow-md cursor-pointer z-10"
                    aria-label={t("bookings.calendar.addBooking", { count: 0 })}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
